import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { getAllColors } from "../../services/color.service";
import { getAllBrands } from "../../services/brand.service";
import { getAllSubCategories } from "../../services/subcategory.service";
import Select from "react-select";
import { getProduct, updateProduct } from "../../services/product.service";

export default function ProductUpdate({ initialData }) {
  const navigate = useNavigate();
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const [designNumbers, setDesignNumbers] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [product, setProduct] = useState(null);
  const [colors, setColors] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const { token } = useSelector((state) => state?.auth);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [uploadImage, setUploadImage] = useState(null);
  const productID = queryParams.get("id");
  const backPage = queryParams.get("page");
  const search = queryParams.get("search");

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    defaultValues: initialData,
  });

  const {
    register: register2,
    handleSubmit: handleSubmitDesignNumber,
    reset: reset2,
    formState: { errors: errors2 },
  } = useForm();

  const fetchColors = async () => {
    try {
      const res = await getAllColors(token);
      if (res && res.status >= 200 && res.status < 300) {
        setColors(res.data);
      } else {
        Swal.fire("Error", "Failed to fetch colors.", "error");
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
      Swal.fire("Error", "Network or server error occurred.", "error");
    }
  };

  useEffect(() => {
    fetchColors();
  }, []);

  const fetchSubCategories = async (page = false) => {
    try {
      const params = { page };
      const res = await getAllSubCategories(token, params);
      if (res && res.status >= 200 && res.status < 300) {
        setSubCategories(res?.data);
      } else {
        Swal.fire(
          "Error",
          res?.data?.message ||
            "Failed to fetch subcategories. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchProduct = async () => {
    try {
      const res = await getProduct(token, productID);

      if (res && res.status >= 200 && res.status < 300) {
        setProduct(res?.data);
      } else {
        Swal.fire(
          "Error",
          "Failed to fetch product details. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching main product:", error);
      Swal.fire(
        "Error",
        "An error occurred while fetching the product. Please try again later.",
        "error"
      );
    }
  };

  useEffect(() => {
    fetchSubCategories();
    if (productID) {
      fetchProduct();
    }
  }, []);

  useEffect(() => {
    console.log(product);

    if (product && subCategories) {
      const selectedSubCategory = subCategories.find(
        (subcategory) =>
          subcategory.sub_category_name === product.subcategory_name
      );
      if (selectedSubCategory) {
        setValue("subcategory", {
          value: selectedSubCategory.sub_category_id,
          label: `${selectedSubCategory.sub_category_name} (${selectedSubCategory.main_category_name})`,
        });
      }
      setValue("productName", product?.product_name);
      setValue("fabric", product?.fabric || null);
      setValue("piecesInSet", product?.pcs_in_set);
      setValue("description", product?.description);
      setValue("assorted", product?.assorted ? "True" : "False");
      setValue("inStock", product?.in_stock ? "True" : "False");
      setValue("brand", product?.brand_name);
      setValue("assortedValue", product?.assorted_pcs_in_set);
      if (product && product?.colors) {
        const initialSelectedColors = product?.colors?.map((color) => ({
          value: color.id,
          label: color.name,
        }));
        setSelectedColors(initialSelectedColors);
        setValue("colors", initialSelectedColors);
      }
      setImagePreview(product?.image);
      if (product.product_design_number) {
        const designNumbersList = product?.product_design_number?.map(
          (design) => design.design_no
        );
        setDesignNumbers(designNumbersList);
        setValue("designNumber1", designNumbersList.join(", "));
      }
    }
  }, [product, subCategories]);

  const onSubmit = async (item) => {
    setIsLoading2(true);

    const data = {
      product_name: item.productName,
      category: item.subcategory?.value || null,
      fabric: item.fabric,
      pcs_in_set: item.piecesInSet,
      in_stock: item.inStock,
      colors: selectedColors.map((color) => color.value),
      description: item.description,
      product_design_number: designNumbers,
      assorted: item.assorted,
      assorted_pcs_in_set: item.assorted === "True" ? item.assortedValue : null,
      image: uploadImage,
    };

    try {
      const res = await updateProduct(token, data, productID);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Updated!",
          text: `${item.productName} products has been updated successfully.`,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate(
            `/products?page=${backPage}${search ? `&search=${search}` : ""}`
          );
        });
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Invalid input. Please check your data.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during updating sub-category:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading2(false);
    }
  };

  const onSubmitDesignNumber = (item) => {
    const delimiters = /[,\s|;:/]+/;
    const newDesignNumbers = item.designNumber
      .split(delimiters)
      .map((num) => num.trim())
      .filter((num) => num !== "");

    if (newDesignNumbers.length > 0) {
      setDesignNumbers((prev) => {
        const uniqueNumbers = [...new Set([...prev, ...newDesignNumbers])];

        setValue("designNumber1", uniqueNumbers.join(", "));
        clearErrors("designNumber1");

        return uniqueNumbers;
      });
    }

    document.getElementById("designModalClose").click();

    reset2();
  };

  const deleteDesignNumber = (number) => {
    setDesignNumbers((prev) => prev.filter((item) => item !== number));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (
        ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(
          file.type
        )
      ) {
        if (file.size < 1 * 1024 * 1024) {
          setImagePreview(URL.createObjectURL(file));
          setUploadImage(file);
          clearErrors("image");
        } else {
          setError("image", {
            type: "manual",
            message: "Image size must be less than 1MB.",
          });
          setUploadImage(null);
          setValue("image", null);
        }
      } else {
        setError("image", {
          type: "manual",
          message: "Only JPEG, PNG, JPG, or WEBP files are allowed.",
        });
        setUploadImage(null);
        setValue("image", null);
      }
    }
  };

  return (
    <div className="content-body">
      {/* Modal for Adding Design Number */}
      <div
        className="modal fade"
        id="addDesignNumberModal"
        tabIndex="-1"
        aria-labelledby="addDesignNumberModalTitle"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="addDesignNumberModalTitle">
                Add Design Number
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                id="designModalClose"
              ></button>
            </div>
            <form onSubmit={handleSubmitDesignNumber(onSubmitDesignNumber)}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Design Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter design number"
                    {...register2("designNumber", {
                      required: "Design number is required",
                    })}
                  />
                  {errors2.designNumber && (
                    <p className="error text-danger">
                      {errors2.designNumber.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary">
                  ADD DESIGN
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* End Modal */}
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-lg-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Update Product</h4>
                <Link
                  to={`/products?page=${backPage}${
                    search ? `&search=${search}` : ""
                  }`}
                >
                  <img
                    src="/assets/images/kidskart/icon/back-button.png"
                    alt="Back"
                  />
                </Link>
              </div>
              <div className="card-body py-3">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">
                    {/* Product Name */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Product Name</label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter product name"
                        {...register("productName", {
                          required: "Product name is required",
                        })}
                      />
                      {errors.productName && (
                        <p className="error">{errors.productName.message}</p>
                      )}
                    </div>

                    {/* Brand */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Brand</label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Brand name"
                        {...register("brand", {
                          required: "Brand is required",
                        })}
                        style={{
                          backgroundColor: "#f8f9fa",
                        }}
                        disabled
                      />
                      {errors.brand && (
                        <p className="error">{errors.brand.message}</p>
                      )}
                    </div>

                    {/* Category */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Category</label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <Select
                        options={subCategories.map((category) => ({
                          value: category.sub_category_id,
                          label: `${category.sub_category_name} (${category.main_category_name})`,
                        }))}
                        {...register("subcategory", {
                          required: "Category is required",
                        })}
                        onChange={(selectedOption) => {
                          setValue("subcategory", selectedOption);
                          clearErrors("subcategory");
                        }}
                        placeholder="Select Category"
                        isClearable
                        value={watch("subcategory")}
                      />

                      {errors.subcategory && (
                        <p className="error">{errors.subcategory.message}</p>
                      )}
                    </div>

                    {/* Design Numbers Input Field */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Design Number</label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Design numbers will appear here"
                          data-bs-toggle="modal"
                          data-bs-target="#addDesignNumberModal"
                          value={designNumbers.join(", ")} // Display the concatenated design numbers
                          {...register("designNumber1", {
                            required: "Design number is required",
                          })}
                          readOnly
                        />
                        <button
                          type="button"
                          className="btn btn2 p-1 ps-2"
                          data-bs-toggle="modal"
                          data-bs-target="#addDesignNumberModal"
                          // title="Add Design Number"
                        >
                          <img src="/assets/images/kidskart/icon/plus.png" />
                          <span className="tooltip-text mb-1">
                            Add Design Number
                          </span>
                        </button>
                        {designNumbers.length > 0 && (
                          <button
                            type="button"
                            className="btn btn2 p-1"
                            onClick={() => setIsDropdownOpen((prev) => !prev)}
                            // title="Delete Design Number"
                          >
                            <img src="/assets/images/kidskart/icon/minus.png" />
                            <span className="tooltip-text mb-1">
                              Delete Design Number
                            </span>
                          </button>
                        )}
                      </div>
                      {errors.designNumber1 && (
                        <p className="error">{errors.designNumber1.message}</p>
                      )}

                      {/* Dropdown for Deleting Design Numbers */}
                      {isDropdownOpen && designNumbers.length > 0 && (
                        <ul className="list-group mt-2">
                          {designNumbers.map((number, index) => (
                            <li
                              key={index}
                              className="list-group-item d-flex justify-content-between align-items-center ps-3 p-1"
                            >
                              {number}
                              <button
                                type="button"
                                className="btn p-0 btn2 py-1"
                                onClick={() => deleteDesignNumber(number)}
                                title="Delete Design Number"
                              >
                                <img src="/assets/images/kidskart/icon/minus.png" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {/* Fabric */}
                    <div className="mb-4 col-md-6">
                      <label className="form-label">Fabric</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter fabric"
                        {...register("fabric")}
                      />
                      {errors.fabric && (
                        <p className="error">{errors.fabric.message}</p>
                      )}
                    </div>

                    {/* Pieces in Set */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Pieces In Set</label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter pieces in set"
                        {...register("piecesInSet", {
                          required: "Pieces in set is required",
                        })}
                      />
                      {errors.piecesInSet && (
                        <p className="error">{errors.piecesInSet.message}</p>
                      )}
                    </div>

                    {/* Assorted */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Assorted</label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <Select
                        options={[
                          { value: "True", label: "Yes" },
                          { value: "False", label: "No" },
                        ]}
                        {...register("assorted", {
                          required: "Assorted status is required",
                        })}
                        onChange={(selectedOption) => {
                          setValue("assorted", selectedOption?.value);
                          clearErrors("assorted");
                        }}
                        placeholder="Select Assorted or Not"
                        isClearable
                        value={{
                          value: watch("assorted"),
                          label: watch("assorted") === "True" ? "Yes" : "No",
                        }}
                      />
                      {errors.assorted && (
                        <p className="error">{errors.assorted.message}</p>
                      )}
                    </div>

                    {/* Conditional Input for Assorted Value */}
                    {watch("assorted") === "True" && (
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <div className="d-flex">
                            <label className="form-label">
                              Assorted Pieces in set
                            </label>
                            <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                          </div>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          {...register("assortedValue", {
                            required: "Assorted Pieces in set is required",
                          })}
                          placeholder="Enter assorted Pieces in set"
                        />
                        {errors.assortedValue && (
                          <p className="error">
                            {errors.assortedValue.message}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Colors Multi-select */}
                    <div className="mb-4 col-md-6">
                      <label className="form-label">Colors</label>
                      <Select
                        isMulti
                        options={colors?.map((color) => ({
                          value: color.color_id,
                          label: color.color_name,
                        }))}
                        value={selectedColors}
                        onChange={(selected) => {
                          setSelectedColors(selected);
                          setValue(
                            "colors",
                            selected.map((color) => color.value)
                          );
                        }}
                      />
                    </div>

                    {/* In Stock */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">In Stock</label>
                        <i className="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <Select
                        options={[
                          { value: "True", label: "Yes" },
                          { value: "False", label: "No" },
                        ]}
                        {...register("inStock", {
                          required: "In stock status is required",
                        })}
                        onChange={(selectedOption) => {
                          setValue("inStock", selectedOption?.value);
                          clearErrors("inStock");
                        }}
                        placeholder="Select Stock Status"
                        isClearable
                        value={{
                          value: watch("inStock"),
                          label: watch("inStock") === "True" ? "Yes" : "No",
                        }}
                      />
                      {errors.inStock && (
                        <p className="error">{errors.inStock.message}</p>
                      )}
                    </div>

                    {/* Image */}
                    <div className="mb-4 col-md-6">
                      <div className="d-flex">
                        <label className="form-label">Image </label>
                        <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                      </div>
                      <input
                        type="file"
                        className="form-control"
                        {...register("image")}
                        onChange={handleImageChange}
                      />
                      {errors.image && (
                        <p className="error">{errors.image.message}</p>
                      )}
                    </div>

                    {watch("assorted") === "True" ? null : (
                      <div className="mb-4 col-md-6"></div>
                    )}

                    <div className="mb-4 col-md-6">
                      {imagePreview && (
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="img-thumbnail mt-2"
                          style={{ width: "100px" }}
                        />
                      )}
                    </div>

                    {/* Description */}
                    <div className="mb-4 col-md-12">
                      <label className="form-label">Description</label>
                      <textarea
                        rows="3"
                        className="form-control"
                        placeholder="Enter description"
                        {...register("description")}
                      ></textarea>
                    </div>
                  </div>

                  {!isLoading2 ? (
                    <div className="col-sm-4">
                      <button type="submit" className="btn btn-primary w-100">
                        UPDATE PRODUCT
                      </button>
                    </div>
                  ) : (
                    <div className="col-sm-4">
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        disabled
                      >
                        UPDATING PRODUCT
                      </button>

                      <div
                        className="spinner-border2 text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
